import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CheckCircle, ArrowLeft, ArrowRight, LockKeyhole, ShieldCheck, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import CustomerInfoForm from './CustomerInfoForm';
import toast from 'react-hot-toast';

// Load Stripe outside of the component so a new instance isn't created on every render.
const stripePromise = loadStripe("pk_live_51RsUY34PgokOsVyKyeb4R78mDX92FZNf8onIegqG3d3VQjiox5qLbs5VDMqMoQ4kjUuqE8YcZlZqTT8wqeUVAgkh00vmid4hAX");

/**
 * Receipt component to display purchase details after successful payment
 */
function Receipt({ purchaseDetails, total, customerInfo }) {
  const currentDate = new Date().toLocaleDateString('no-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <motion.div 
      className="receipt"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <motion.div 
        className="receipt-header"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <h2>
          <CheckCircle size={32} style={{ display: 'inline', marginRight: '0.5rem' }} />
          Kvittering
        </h2>
        <p>Takk for kjøpet, {customerInfo?.fullName || 'kunde'}!</p>
      </motion.div>
      
      <motion.div 
        className="receipt-details"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="receipt-info">
          <p><strong>Dato:</strong> {currentDate}</p>
          <p><strong>Betalings-ID:</strong> {purchaseDetails.paymentIntentId}</p>
          <p><strong>Status:</strong> <span style={{ color: '#48bb78', fontWeight: 'bold' }}>Betalt</span></p>
        </div>

        {/* Customer Information */}
        {customerInfo && (
          <div className="customer-info-section">
            <h3>Kunde- og leveringsinformasjon:</h3>
            <div className="customer-details">
              <p><strong>Navn:</strong> {customerInfo.fullName}</p>
              <p><strong>E-post:</strong> {customerInfo.email}</p>
              <p><strong>Telefon:</strong> {customerInfo.phone}</p>
              {customerInfo.shippingMethod === 'pickup' ? (
                <>
                  <p><strong>Hentested:</strong> Bølerveien 24B (Lager)</p>
                  <p>Vi kontakter deg for å avtale hentetidspunkt.</p>
                </>
              ) : (
                <>
                  <p><strong>Leveringsadresse:</strong></p>
                  <div className="address-details">
                    <p>{customerInfo.address}</p>
                    <p>{customerInfo.postalCode} {customerInfo.city}</p>
                    <p>{customerInfo.country}</p>
                  </div>
                </>
              )}
              {customerInfo.specialInstructions && (
                <p><strong>Spesielle instruksjoner:</strong> {customerInfo.specialInstructions}</p>
              )}
            </div>
          </div>
        )}

        <div className="receipt-items">
          <h3>Kjøpte varer:</h3>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <AnimatePresence>
              {purchaseDetails.items.map((item, index) => (
                <motion.li 
                  key={index} 
                  className="receipt-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
                >
                  <div className="item-details">
                    <span className="item-name">{item.name}</span>
                    <span className="item-quantity">Antall: {item.quantity}</span>
                  </div>
                  <div className="item-pricing">
                    <span className="item-price">
                      {item.price.toLocaleString('no-NO', {
                        style: 'currency',
                        currency: 'NOK',
                      })} per stk
                    </span>
                    <span className="item-total">
                      {item.total.toLocaleString('no-NO', {
                        style: 'currency',
                        currency: 'NOK',
                      })}
                    </span>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        </div>

        <motion.div 
          className="receipt-summary"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <div className="summary-line">
            <span>Antall varer:</span>
            <span>{purchaseDetails.totalQuantity}</span>
          </div>
          <div className="summary-line total-line">
            <span><strong>Totalt betalt:</strong></span>
            <span><strong>{total.toLocaleString('no-NO', {
              style: 'currency',
              currency: 'NOK',
            })}</strong></span>
          </div>
        </motion.div>

        <motion.div 
          className="receipt-footer"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          {customerInfo?.shippingMethod === 'pickup' ? (
            <>
              <p>Ordren er registrert for personlig henting på Bølerveien 24B.</p>
              <p>Kontakt oss om hentetidspunkt.</p>
            </>
          ) : (
            <>
              <p>Varene sendes til adressen du oppga ved bestilling.</p>

            </>
          )}
          <p>Ved spørsmål, kontakt oss på support@bmwplug.no</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

/**
 * Internal component to render the Stripe payment form. Handles form submission
 * and displays status messages to the user. After a successful payment the
 * cart is cleared.
 */
function CheckoutForm({ total, cart, onPaymentSuccess, customerInfo }) {
  const stripe = useStripe();
  const elements = useElements();
  const { clearCart } = useCart();
  const [message, setMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

  const isCustomerInfoValid = customerInfo &&
    customerInfo.fullName &&
    customerInfo.email &&
    customerInfo.phone &&
    (
      customerInfo.shippingMethod === 'pickup' ||
      (customerInfo.address && customerInfo.city && customerInfo.postalCode)
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isCustomerInfoValid) {
      setMessage('Vennligst fyll ut all nødvendig kundesinformasjon før du fortsetter med betalingen.');
      return;
    }
    
    if (!stripe || !elements) {
      setMessage('Stripe er ikke lastet ennå. Vennligst vent et øyeblikk og prøv igjen.');
      return;
    }

    // Ensure the unified Payment Element is mounted before confirming payment
    const paymentElement = elements.getElement(PaymentElement);
    if (!paymentElement) {
      setMessage('Betalingselementet er ikke klart ennå. Vent et øyeblikk og prøv igjen.');
      return;
    }
    
    setIsProcessing(true);
    setMessage(null); // Clear any previous messages
    const loadingToast = toast.loading('Behandler betaling...');
    
    try {
      
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout`,
          payment_method_data: {
            billing_details: {
              name: customerInfo.fullName,
              email: customerInfo.email,
              phone: customerInfo.phone,
              address: customerInfo.shippingMethod === 'pickup' ? undefined : {
                line1: customerInfo.address,
                city: customerInfo.city,
                postal_code: customerInfo.postalCode,
                country: customerInfo.country === 'Norge' ? 'NO' : 
                         customerInfo.country === 'Sverige' ? 'SE' :
                         customerInfo.country === 'Danmark' ? 'DK' :
                         customerInfo.country === 'Finland' ? 'FI' : 'NO'
              }
            }
          }
        },
        redirect: 'if_required'
      });
      
      
      if (error) {
        console.error('Payment error:', error);
        let errorMessage = 'En feil oppstod under betalingen.';
        
        // Provide more specific error messages
        switch (error.type) {
          case 'card_error':
            errorMessage = `Kortfeil: ${error.message}`;
            break;
          case 'validation_error':
            errorMessage = `Valideringsfeil: ${error.message}`;
            break;
          case 'invalid_request_error':
            errorMessage = 'Ugyldig forespørsel. Vennligst prøv igjen.';
            break;
          case 'api_connection_error':
            errorMessage = 'Tilkoblingsproblem. Sjekk internettforbindelsen din.';
            break;
          case 'api_error':
            errorMessage = 'Server feil. Vennligst prøv igjen senere.';
            break;
          case 'authentication_error':
            errorMessage = 'Autentiseringsfeil. Kontakt support.';
            break;
          case 'rate_limit_error':
            errorMessage = 'For mange forespørsler. Vennligst vent og prøv igjen.';
            break;
          default:
            errorMessage = error.message || 'En ukjent feil oppstod.';
        }
        
        setMessage(errorMessage);
        toast.error(errorMessage, { id: loadingToast });
      } else {
        toast.success('Betalingen ble gjennomført!', { id: loadingToast });
        
  // Call the onPaymentSuccess prop to notify the parent component
  // Pass a snapshot of the items so the receipt can be built even if the cart is cleared later
  onPaymentSuccess(paymentIntent, cart?.items || []);
        
  // Clear the cart after notifying parent (to keep receipt data intact)
  clearCart();
      }
    } catch (err) {
      setIsProcessing(false);
      setMessage('En feil oppstod under betalingen. Vennligst prøv igjen.');
      toast.error('En feil oppstod under betalingen. Vennligst prøv igjen.', { id: loadingToast });
      console.error('Payment processing error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <PaymentElement onReady={() => setIsPaymentElementReady(true)} />
      <div className="form-actions">
        <button type="submit" disabled={!stripe || !isPaymentElementReady || isProcessing} className="submit-button">
          {isProcessing ? 'Behandler...' : 'Fullfør betaling'}
        </button>
      </div>
      {message && <div className="status-message">{message}</div>}
    </form>
  );
}

export default function Checkout() {
  const { cart, clearCart } = useCart();
  const cartItems = cart?.items || [];
  const cartTotal = cartItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const hasInquiryItem = cartItems.some(item => item.available === false || !Number(item.price));
  const [customerInfo, setCustomerInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('bmwplug-checkout-info')) || { shippingMethod: 'delivery' }; }
    catch { return { shippingMethod: 'delivery' }; }
  });
  const [paymentStep, setPaymentStep] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [piError, setPiError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isCreatingPI, setIsCreatingPI] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [paidTotal, setPaidTotal] = useState(0);

  const handlePaymentSuccess = async (paymentIntent, purchasedItems) => {
    const items = (purchasedItems || []).map(item => ({ name: item.name, price: Number(item.price), quantity: item.quantity, total: Number(item.price) * item.quantity }));
    setPurchaseDetails({ paymentIntentId: paymentIntent.id, items, totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0) });
    setPaidTotal(paymentIntent.amount / 100);
    sessionStorage.removeItem('bmwplug-checkout-info');
    try { await axios.post('/api/payment-success', { paymentIntentId: paymentIntent.id }); }
    catch (error) { console.error('Could not log payment success:', error); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnedSecret = params.get('payment_intent_client_secret');
    if (!returnedSecret) return;
    let cancelled = false;
    async function finishRedirect() {
      try {
        const stripe = await stripePromise;
        const { paymentIntent } = await stripe.retrievePaymentIntent(returnedSecret);
        if (!cancelled && paymentIntent?.status === 'succeeded') {
          await handlePaymentSuccess(paymentIntent, cartItems);
          clearCart();
        }
      } catch (error) { console.error('Could not verify redirected payment:', error); }
      finally { window.history.replaceState({}, '', window.location.pathname); }
    }
    finishRedirect();
    return () => { cancelled = true; };
    // Redirect verification must run once with the restored cart snapshot.
  }, []);

  useEffect(() => {
    if (!paymentStep || !cartItems.length || hasInquiryItem) return;
    let cancelled = false;
    async function createPaymentIntent() {
      setIsCreatingPI(true);
      setPiError(null);
      try {
        const response = await axios.post('/api/create-payment-intent', {
          items: { items: cartItems.map(item => ({ id: item.id, quantity: item.quantity })) },
          customerInfo,
        });
        if (!cancelled) setClientSecret(response.data.clientSecret);
      } catch (error) {
        if (!cancelled) setPiError('Kunne ikke klargjøre betaling. Prøv igjen eller kontakt oss.');
      } finally { if (!cancelled) setIsCreatingPI(false); }
    }
    createPaymentIntent();
    return () => { cancelled = true; };
    // Payment step freezes the submitted customer data until it is explicitly edited.
  }, [paymentStep]);

  function continueToPayment() {
    const info = customerInfo;
    const valid = info.fullName?.trim() && /\S+@\S+\.\S+/.test(info.email || '') && /^[\d\s+()\-]{8,}$/.test(info.phone || '') &&
      (info.shippingMethod === 'pickup' || (info.address?.trim() && info.city?.trim() && /^\d{4}$/.test(info.postalCode || '')));
    if (!valid) { setFormError('Fyll ut navn, gyldig e-post, telefon og leveringsadresse før du fortsetter.'); return; }
    setFormError(null);
    sessionStorage.setItem('bmwplug-checkout-info', JSON.stringify(info));
    setPaymentStep(true);
  }

  if (purchaseDetails) return <div className="checkout-surface"><Receipt purchaseDetails={purchaseDetails} total={paidTotal} customerInfo={customerInfo} /></div>;

  return <main className="checkout-surface"><div className="checkout-shell">
    <div className="bag-breadcrumb"><Link to="/">Butikk</Link><span>/</span><Link to="/cart">Handlekurv</Link><span>/</span><span>Kasse</span></div>
    <div className="checkout-heading"><div className="eyebrow"><span/> TRYGG UTSJEKK</div><h1>Fullfør bestillingen<span>.</span></h1><p>Kontroller opplysningene dine og se over varene før betaling.</p></div>
    {cartItems.length === 0 ? <div className="checkout-empty"><h2>Handlekurven er tom</h2><p>Legg til en vare før du går til kassen.</p><Link className="bag-primary" to="/">Se produkter <ArrowRight size={18}/></Link></div> : hasInquiryItem ? <div className="checkout-empty"><h2>En vare må forespørres</h2><p>Varer uten fast pris kan ikke betales i kassen. Fjern varen fra handlekurven eller kontakt oss for pris.</p><Link className="bag-primary" to="/cart">Tilbake til handlekurven <ArrowRight size={18}/></Link></div> : <div className="checkout-layout">
      <section className="checkout-main">
        <div className="checkout-step-heading"><span>01</span><div><h2>Kontakt og levering</h2><p>{paymentStep ? 'Opplysningene er klare for betaling.' : 'Fyll inn informasjonen vi trenger for ordren.'}</p></div>{paymentStep && <button onClick={() => { setPaymentStep(false); setClientSecret(null); setPiError(null); }}>Endre</button>}</div>
        {paymentStep ? <div className="checkout-customer-review"><strong>{customerInfo.fullName}</strong><span>{customerInfo.email} · {customerInfo.phone}</span><span>{customerInfo.shippingMethod === 'pickup' ? 'Henting på Bølerveien 24B' : `${customerInfo.address}, ${customerInfo.postalCode} ${customerInfo.city}`}</span></div> : <><CustomerInfoForm customerInfo={customerInfo} onInfoChange={setCustomerInfo}/>{formError && <p className="checkout-error" role="alert">{formError}</p>}<button className="bag-primary checkout-next" onClick={continueToPayment}>Fortsett til betaling <ArrowRight size={18}/></button></>}
        <div className="checkout-step-heading second"><span>02</span><div><h2>Betaling</h2><p>Betal sikkert når opplysningene er kontrollert.</p></div></div>
        {paymentStep ? <div className="checkout-payment">{piError && <p className="checkout-error" role="alert">{piError} <button onClick={() => { setPaymentStep(false); setClientSecret(null); }}>Prøv på nytt</button></p>}{clientSecret ? <Elements stripe={stripePromise} options={{ clientSecret, locale: 'no' }} key={clientSecret}><CheckoutForm total={cartTotal} cart={{ items: cartItems }} onPaymentSuccess={handlePaymentSuccess} customerInfo={customerInfo}/></Elements> : !piError && <p className="checkout-loading">{isCreatingPI ? 'Klargjør sikker betaling …' : 'Laster betaling …'}</p>}</div> : <div className="checkout-locked"><LockKeyhole size={18}/> Fyll ut kontaktinformasjonen for å åpne betaling.</div>}
        <Link className="checkout-back" to="/cart"><ArrowLeft size={16}/> Tilbake til handlekurven</Link>
      </section>
      <aside className="checkout-order"><span className="checkout-overline">BESTILLINGEN DIN</span><h2>Ordreoversikt</h2><div className="checkout-order-items">{cartItems.map(item => <div className="checkout-order-item" key={item.id}><div className="checkout-order-image">{(item.images?.[0] || item.image) && <img src={item.images?.[0] || item.image} alt=""/>}</div><div><b>{item.name}</b><span>Antall: {item.quantity}</span></div><strong>{(Number(item.price) * item.quantity).toLocaleString('no-NO')} kr</strong></div>)}</div><div className="checkout-order-lines"><div><span>Varer</span><strong>{cartTotal.toLocaleString('no-NO')} kr</strong></div><div><span>Frakt</span><span>Ikke inkludert</span></div></div><div className="checkout-order-total"><span>Å betale nå</span><strong>{cartTotal.toLocaleString('no-NO')} kr</strong></div><p className="checkout-order-note">Frakt er ikke inkludert i betalingen. Kontakt oss om leveringskostnad før du bestiller.</p><div className="checkout-assurance"><div><ShieldCheck size={19}/> Trygg betaling</div><div><Truck size={19}/> Levering eller henting</div></div></aside>
    </div>}
  </div></main>;
}
